import _ from "lodash";

const timeouts = [200, 500, 1000, 1000, 1000, 1000, 1000, 1000, 1000];
const bottomSpacerGap = 20;

function getMainElements(window, formButtonsEl?) {
  const el = formButtonsEl && formButtonsEl.isConnected
    ? formButtonsEl
    : window.document.getElementsByClassName("formButtons")[0];
  const mcel = window.document.getElementsByClassName("mainContentContainer")[0];
  if (!el || !el.isConnected || !mcel) {
    return null;
  }
  return {el: el, mcel: mcel};
}

function clearBottomSpacer(mcel) {
  mcel.style.paddingBottom = '';
}

function setBottomSpacer(mcel, el) {
  const height = el.offsetHeight;
  mcel.style.paddingBottom = height > 0 ? height + bottomSpacerGap + 'px' : '';
}

function unstickButtons(mcel) {
  mcel.classList.remove("fixedFormButtons");
  clearBottomSpacer(mcel);
}

function isFullyInViewport(rect, window) {
  const height = window.innerHeight || window.document.documentElement.clientHeight;
  const width = window.innerWidth || window.document.documentElement.clientWidth;
  return rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= height &&
    rect.right <= width;
}

/**
 * Pin .formButtons to the bottom of the viewport when they are below the fold.
 * Unsticks when scrolling brings the buttons back into their natural position.
 * Returns a cleanup function that clears the fixed state and cancels retries.
 */
export function formButtonVisibleCheck(window, onSubmitProcessed, formButtonsEl?) {
  let index = 0;
  let destroyed = false;
  let naturalAnchor = null;
  let scrollScheduled = false;
  const pendingTimeouts = [];

  function schedule(fn, delay?) {
    const handle = setTimeout(function() {
      if (!destroyed) {
        fn();
      }
    }, delay);
    pendingTimeouts.push(handle);
    return handle;
  }

  function cancelPending() {
    _.forEach(pendingTimeouts, function(handle) {
      if (handle) {
        clearTimeout(handle);
      }
    });
    pendingTimeouts.length = 0;
  }

  function captureNaturalAnchor(el, window) {
    const scrollY = window.pageYOffset || window.document.documentElement.scrollTop;
    const scrollX = window.pageXOffset || window.document.documentElement.scrollLeft;
    const rect = el.getBoundingClientRect();
    naturalAnchor = {
      docTop: rect.top + scrollY,
      docBottom: rect.bottom + scrollY,
      docLeft: rect.left + scrollX,
      docRight: rect.right + scrollX
    };
  }

  function naturalViewportRect(window) {
    if (!naturalAnchor) {
      return null;
    }
    const scrollY = window.pageYOffset || window.document.documentElement.scrollTop;
    const scrollX = window.pageXOffset || window.document.documentElement.scrollLeft;
    return {
      top: naturalAnchor.docTop - scrollY,
      bottom: naturalAnchor.docBottom - scrollY,
      left: naturalAnchor.docLeft - scrollX,
      right: naturalAnchor.docRight - scrollX
    };
  }

  /** Keep spacer height in sync when validation messages change the button block size. */
  function refreshBottomSpacer(mcel, el) {
    schedule(function() {
      if (destroyed || !mcel.classList.contains("fixedFormButtons")) {
        return;
      }
      setBottomSpacer(mcel, el);
    }, 300);
  }

  function stickButtons(mcel, el) {
    if (!mcel.classList.contains("fixedFormButtons")) {
      mcel.classList.add("fixedFormButtons");
    }
    setBottomSpacer(mcel, el);
    refreshBottomSpacer(mcel, el);
  }

  function measureInDocumentFlow(el, mcel, window) {
    const wasFixed = mcel.classList.contains("fixedFormButtons");
    if (wasFixed) {
      unstickButtons(mcel);
    }
    const rect = el.getBoundingClientRect();
    captureNaturalAnchor(el, window);
    return rect;
  }

  function scheduleRetry(fromScroll) {
    if (!fromScroll && timeouts[index]) {
      schedule(doButtonCheck, timeouts[index]);
      index++;
    }
  }

  function doButtonCheck(options) {
    if (destroyed) {
      return;
    }

    const fromScroll = !!(options && options.fromScroll);
    const forceMeasure = !!(options && options.forceMeasure);
    const elements = getMainElements(window, formButtonsEl);
    if (!elements) {
      // Layout/content can lag SPA activation; keep retrying briefly.
      scheduleRetry(fromScroll);
      return;
    }

    const el = elements.el;
    const mcel = elements.mcel;
    const isFixed = mcel.classList.contains("fixedFormButtons");

    let rect;
    if (isFixed && !forceMeasure && naturalAnchor) {
      rect = naturalViewportRect(window);
    } else {
      rect = measureInDocumentFlow(el, mcel, window);
    }

    if (!rect) {
      scheduleRetry(fromScroll);
      return;
    }

    const isInViewport = isFullyInViewport(rect, window);

    if (!isInViewport) {
      stickButtons(mcel, el);
    } else {
      if (isFixed) {
        measureInDocumentFlow(el, mcel, window);
      }
      scheduleRetry(fromScroll);
    }
  }

  function onScrollOrResize(event) {
    if (destroyed || scrollScheduled) {
      return;
    }
    scrollScheduled = true;
    const runCheck = function() {
      scrollScheduled = false;
      if (!destroyed) {
        doButtonCheck({
          fromScroll: true,
          forceMeasure: event && event.type === 'resize'
        });
      }
    };
    if (_.isFunction(window.requestAnimationFrame)) {
      window.requestAnimationFrame(runCheck);
    } else {
      schedule(runCheck, 0);
    }
  }

  schedule(doButtonCheck);
  const unsubSubmit = onSubmitProcessed(function() {
    doButtonCheck({forceMeasure: true});
  });

  window.addEventListener('scroll', onScrollOrResize, {passive: true});
  window.addEventListener('resize', onScrollOrResize, {passive: true});

  function cleanup() {
    if (destroyed) {
      return;
    }
    destroyed = true;
    cancelPending();
    // mainContentContainer is shared across SPA navigations. Only clear the
    // fixed class if we still own the live .formButtons (or none remain) —
    // otherwise an outgoing page's destroy wipes the next page's sticky pin.
    const liveFirst = window.document.getElementsByClassName("formButtons")[0];
    if (!liveFirst || !formButtonsEl || liveFirst === formButtonsEl) {
      const mcel = window.document.getElementsByClassName("mainContentContainer")[0];
      if (mcel) {
        unstickButtons(mcel);
      }
    }
    naturalAnchor = null;
    window.removeEventListener('scroll', onScrollOrResize);
    window.removeEventListener('resize', onScrollOrResize);
    if (_.isFunction(unsubSubmit)) {
      unsubSubmit();
    }
  }

  return cleanup;
}
